import { Button, ButtonSize, ButtonVariant, Input, Shape, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import {
  PUBLIC_KINDE_CONNECTION_APPLE,
  PUBLIC_KINDE_CONNECTION_DISCORD,
  PUBLIC_KINDE_CONNECTION_EMAIL_PASSWORDLESS,
  PUBLIC_KINDE_CONNECTION_GOOGLE,
  PUBLIC_KINDE_CONNECTION_X,
} from "@next-platform/client-common-core/infra/constants";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

import { SiteLogo } from "@/components";
import { AppleIcon, DiscordIcon, GoogleIcon, XIcon } from "@/components/icons";
import Trans from "@/components/Trans/Trans";
import { NEXT_PAGE_UPON_REDIRECT, ROUTES, SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY } from "@/constants";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import useAuth from "@/services/useAuth";
import { validateEmail } from "@/utils";

const socialButtons = [
  {
    connectionId: PUBLIC_KINDE_CONNECTION_GOOGLE,
    Icon: GoogleIcon,
    label: "Google",
  },
  {
    connectionId: PUBLIC_KINDE_CONNECTION_APPLE,
    Icon: AppleIcon,
    label: "Apple",
  },
  {
    connectionId: PUBLIC_KINDE_CONNECTION_X,
    Icon: XIcon,
    label: "X",
  },
  {
    connectionId: PUBLIC_KINDE_CONNECTION_DISCORD,
    Icon: DiscordIcon,
    label: "Discord",
  },
];

interface ISocialLoginButtonProps {
  connectionId: string | undefined;
  Icon: React.ComponentType;
  label: string;
  disabled: boolean;
  loading: boolean;
  onClick: (connectionId?: string) => void;
}

const SocialLoginButton = ({ connectionId, Icon, label, disabled, loading, onClick }: ISocialLoginButtonProps) => {
  const { t } = useTranslation();
  const ariaLabel = t("auth:signIn.continueWith.aria", { button: label });
  const transValues = useMemo(() => ({ platform: label }), [label]);
  const handleClick = useCallback(() => onClick(connectionId), [onClick, connectionId]);

  return (
    <Button
      disabled={disabled}
      variant={ButtonVariant.OUTLINED}
      shape={Shape.SQUARE}
      size={ButtonSize.LARGE}
      aria-label={ariaLabel}
      onClick={handleClick}
      loading={loading}
      className="relative"
    >
      <div className="absolute top-0 left-3 translate-y-[50%]">
        <Icon />
      </div>
      <Trans i18nKey="auth:signIn.continueWith" values={transValues} />
    </Button>
  );
};

type LoginContetProps = {
  hideSignInLabel?: boolean;
  className?: string;
};

const LoginContent = ({ hideSignInLabel = false, className }: LoginContetProps) => {
  const { t } = useTranslation();
  const [stateEmail, setStateEmail] = useState("");
  const [stateIsError, setStateIsError] = useState(false);
  const [stateLoginMethod, setStateLoginMethod] = useState("");
  const { login } = useAuth();
  const isEmailLoginLoading = stateLoginMethod === PUBLIC_KINDE_CONNECTION_EMAIL_PASSWORDLESS;
  const localizePathname = useLocalizedPathname();

  const persistRouteUponRedirect = useCallback(() => {
    const currentFullPath = `${window.location.pathname}${window.location.search}`;
    const isLoginPage = window.location.pathname.endsWith(ROUTES.login);
    const redirectPath = isLoginPage ? localizePathname(ROUTES.home) : currentFullPath;

    localStorage.setItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY, "true");
    localStorage.setItem(NEXT_PAGE_UPON_REDIRECT, redirectPath);
  }, [localizePathname]);

  const onChange = useCallback((text: string) => {
    setStateIsError(false);
    setStateEmail(text);
  }, []);

  const onClickSubmit = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const connectionId = PUBLIC_KINDE_CONNECTION_EMAIL_PASSWORDLESS || "";
      if (!stateEmail) {
        setStateIsError(true);
        event.preventDefault();
      } else if (!validateEmail(stateEmail)) {
        setStateIsError(true);
        event.preventDefault();
      } else {
        setStateLoginMethod(connectionId);

        persistRouteUponRedirect();

        login({
          authUrlParams: {
            connectionId,
            loginHint: stateEmail,
          },
        });
      }
    },
    [stateEmail, login, persistRouteUponRedirect],
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      setStateLoginMethod("");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const onClickSocial = useCallback(
    (connectionId?: string) => {
      if (connectionId) {
        setStateLoginMethod(connectionId);
      }

      persistRouteUponRedirect();
      login({
        authUrlParams: {
          connectionId,
        },
      });
    },
    [login, persistRouteUponRedirect],
  );

  return (
    <div className={className}>
      <div className="flex items-center justify-center relative flex-row">
        <SiteLogo type="icon" />
      </div>
      <div className="flex flex-col py-4">
        {!hideSignInLabel && (
          <Text variant={TextVariants.MOBILE_HEADING_4_BOLD}>
            <Trans i18nKey="common:signIn" />
          </Text>
        )}
        <Text variant={TextVariants.PARAGRAPH_SMALL_MEDIUM} className="text-gray-11">
          <Trans i18nKey="auth:signIn.description" />
        </Text>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM}>
            <Trans i18nKey="common:email" />
          </Text>
          <Input placeholder="" onChange={onChange} error={stateIsError} aria-required />
        </div>
        <Button
          disabled={!!stateLoginMethod}
          shape={Shape.SQUARE}
          size={ButtonSize.LARGE}
          onClick={onClickSubmit}
          aria-label={t("common:continue")}
          loading={isEmailLoginLoading}
        >
          <Trans i18nKey="common:continue" />
        </Button>
      </div>
      <div className="flex flex-col py-4">
        <Text variant={TextVariants.PARAGRAPH_SMALL_MEDIUM} className="text-center">
          <Trans i18nKey="common:or" />
        </Text>
      </div>
      <div className="flex flex-col gap-3">
        {socialButtons.map((button, index) => (
          <SocialLoginButton
            key={index}
            connectionId={button.connectionId}
            Icon={button.Icon}
            label={button.label}
            disabled={!!stateLoginMethod}
            loading={stateLoginMethod === button.connectionId}
            onClick={onClickSocial}
          />
        ))}
      </div>
    </div>
  );
};

export default LoginContent;
